import { at } from '../generated/Beans';
import { PortletStoreApi } from '../App';
import {
  BasePortletStore,
  NotificationStore,
  NotificationStoreApi,
  NotificationType
} from 'ehs-react';
import PageStyleDefaults = at.sense.hooks.notifications.bean.PageStyleDefaults;
import FrontendConfigurationBean = at.sense.hooks.notifications.bean.config.FrontendConfigurationBean;

function mapToastOrMsg(
  param: PageStyleDefaults | undefined,
  fallback: 'modal' | 'toast'
): 'modal' | 'toast' {
  switch (param) {
    case 'TOAST':
      return 'toast';
    case 'MSGBOX':
      return 'modal';
  }
  return fallback;
}

/**
 * Maps the configured notification types per page to the type expected by the NotificationStore.
 *
 * @author Bennett Piater
 * @param config configuration per page
 * @return the mapped result
 */
function mapPageStyleConfiguration(
  config: at.sense.hooks.notifications.bean.PageStyle[]
): Record<NotificationType, 'modal' | 'toast'> {
  const url = PortletStoreApi.getState().url;
  const page = getConfigurationForPage(config, url);

  return {
    [NotificationType.SUCCESS]: mapToastOrMsg(page?.info, 'toast'),
    [NotificationType.INFO]: mapToastOrMsg(page?.info, 'toast'),
    [NotificationType.WARN]: mapToastOrMsg(page?.warn, 'modal'),
    [NotificationType.ERROR]: mapToastOrMsg(page?.error, 'modal')
  };
}

/**
 * Function for mapping the notification/toast configurations to the correct store object type.
 *
 * @author Bennett Piater, agd
 * @param config the FrontendConfigurationBean
 */
function mapConfiguration(
  config: FrontendConfigurationBean | undefined
): NotificationStore['config'] | undefined {
  if (!config) return undefined;
  return {
    notificationType: mapPageStyleConfiguration(config.notifications),
    toastStyle: {
      timeout: config.toaststyle.hideAfter * 1000, // the configuration is in seconds, but we expect milliseconds
      closeButton: config.toaststyle.showCloseButton
    }
  };
}

/**
 * Get the longest matching (AKA the most precise) configuration.
 *
 * @param config the portlet config
 * @param url the current page
 */
function getConfigurationForPage(
  config: at.sense.hooks.notifications.bean.PageStyle[],
  url: string
): at.sense.hooks.notifications.bean.PageStyle | undefined {
  let matchedStyle;
  let matchedLength = 0;

  for (const pageStyle of config) {
    if (
      url.includes(pageStyle.friendlyURL) &&
      pageStyle.friendlyURL.length > matchedLength
    ) {
      matchedStyle = pageStyle;
      matchedLength = pageStyle.friendlyURL.length;
    }
  }

  return matchedStyle;
}

// Update the settings in the shared notification store with the settings loaded by the POE
export function setupStore(): void {
  PortletStoreApi.subscribe(
    (config: BasePortletStore<FrontendConfigurationBean> | null) => {
      console.debug(
        'updating notification settings:',
        config?.configuration?.notifications,
        config?.configuration?.toaststyle
      );
      NotificationStoreApi.getState().updateConfig(
        mapConfiguration(config?.configuration)
      );
    }
  );
}
